import {createSlice} from "@reduxjs/toolkit";
import moment from "moment";

const newVisitDefaultValue = {
    client: "",
    clientName: "",
    cause: "",
    notes: "",

    activeInsurances: [],
    ekasLoss: "false",
    activePartException: {},
}

const activeVisitDefaultValue = {
    client: "",
    clientName: "",
    visitCause: "",
    visitNotes: "",
}

const newPrescriptionDefaultValue = {
    date: new Date(),
    type: "normal", // normal, ifet, consumable, galenical
    repeat: "1",
    repeatPeriod: "",
    monthlyTreatment: false,
    bimonthlyTreatment: false,
    chronicIllness: false,
    zeroParticipationReason: "",
    oneDose: false,
    prescriptionWithCommercialName: false,
    prescriptionWithCommercialNameCause: "",
    prescriptionWithCommercialNameNotes: "",
    medicalOpinion: false,
    medicalOpinionByNumber: false,
    medicalOpinionNumber: "",
    medicalOpinionIdentifier: "",
    medicalOpinionAMKA: "",
    medicalOpinionDate: "", // DD/MM/YYYY
    medicalOpinionSpecialtyId: "",
    ekasLoss: "false",
    activePartException: {}, // Object

    homeDelivery: false,

    diagnoses: [],
    drugs: [], // Medicines for normal prescriptions
    ifet: [], // Medicines for IFET prescriptions
    overrides: [],
    questions: [], // Questions for exam type 3000 - BRCA update

    // Justifications & New fields for exam type 3000 BRCA update
    examJustification: "",

    // Consumables
    consumables: [],
    consumableCategory: "",
}

const newTreatmentProtocolDefaultValue = {
    treatmentProtocolId: "",
    selectedICD10s: [],
    treatmentStep: "",
    treatmentStepNotes: "",
}

const newReferralDefaultValue = {
    type: "1",
    examType: "", // examinationGroupId
    cause: "",
    notes: "",
    zeroParticipationReason: "",
    zeroParticipationReasonNeedsDecision: false,
    needsDecisionDate: "",
    needsDecisionNumber: "",
    ekasLoss: "false",
    activePartException: {}, // Object
    diagnoses: [],
    exams: [],
    overrides: [],
}

const editVisitDefaultValue = {
    id: "0",
    prescriptions: [],
    referrals: [],
}

const editPrescriptionDefaultValue = {
    barcode: "0",
    diagnoses: [],
    drugs: [],
}

const editReferralDefaultValue = {
    barcode: "0",
}

const defaultPatientHistory = {
    client: "",
    clientDisplay: "",
    amka: "",

    date_from: moment().subtract(1, "month").format("DD/MM/YYYY"),
    date_to: moment().format("DD/MM/YYYY"),
    patientsConsent: false,
    patientsConsentTP: false,
    patientsConsentBiometrics: false,
    patientsConsentPersonal: false,
}

const defaultPatientHistoryReq = {
    page: 1,
    totalPages: 0,
    totalItems: 0,
}

const initialState = {
    companyHasPrescription: false,
    newVisit: newVisitDefaultValue,
    newVisitSelectedClient: {},

    editVisit: editVisitDefaultValue,
    editVisitLoading: false,
    editVisitReload: false,

    newPrescription: newPrescriptionDefaultValue,
    newTreatmentProtocol: newTreatmentProtocolDefaultValue,
    newReferral: newReferralDefaultValue,

    activeVisit: activeVisitDefaultValue,
    activeVisitClientData: null,
    activeVisitFirstLoad: true,
    activeVisitPrescriptions: [],
    activeVisitTreatmentProtocols: [],
    activeVisitTreatmentProtocolsSteps: [],
    activeVisitReferrals: [],
    activeVisitForceRefresh: false, // Refreshing Prescriptions & Referrals

    editPrescription: editPrescriptionDefaultValue,
    editPrescriptionLoading: false,
    editPrescriptionReload: false,

    editReferral: editReferralDefaultValue,
    editReferralLoading: false,
    editReferralReload: false,

    doctorTreatmentProtocols: [],

    searchVisits: [],
    totalItemsV: 0,
    perPageV: 0,

    searchPrescriptions: [],
    totalItemsP: 0,
    perPageP: 0,

    searchReferrals: [],
    totalItemsR: 0,
    perPageR: 0,

    searchICD10s: [],
    totalItemsICD10s: 0,
    perPageICD10s: 0,

    searchDrugs: [],
    totalItemsDrugs: 0,
    perPageDrugs: 0,

    searchExams: [],
    totalItemsExams: 0,
    perPageExams: 0,

    prescriptionHome: {
        firstLoad: true,
        totalNoInnValue: 0,
        totalValue: 0,
        actualCost: 0,
        eoppyCostLimit: 0,
        announcements: [],
    },

    newVisitOverrideModal: {
        show: false,
        text: "",
        overrideTypeId: "",
        overrideReasonId: "",
        isChecked: "",
    },

    patientHistory: {...defaultPatientHistory},
    patientHistoryOptions: {
        tps: false,
        biometrics: false,
        personal: false,
    },
    patientHistoryReferrals: [],
    patientHistoryReferralsRequestData: {...defaultPatientHistoryReq},
    patientHistoryPrescriptions: [],
    patientHistoryPrescriptionsRequestData: {...defaultPatientHistoryReq},
    patientHistoryTreatmentProtocols: [],
    patientHistoryFirstLoad: true,
    autoPatientHistory: null, // object of client

    // Reports
    prescriptionsReportRequestData: {
        page: 1,
        dateType: "startDate",
        date_from: moment().startOf("month").format("DD/MM/YYYY"),
        date_to: moment().endOf("month").format("DD/MM/YYYY"),
        barcode: "",
        identifier: "",
        medicine: "",
        status: "",
    },
    prescriptionsReportResponseData: {
        data: [],
        totalItems: 0,
        totalPages: 0,
    },

    drugReportRequestData: {
        dateType: "startDate",
        date_from: moment().startOf("month").format("DD/MM/YYYY"),
        date_to: moment().endOf("month").format("DD/MM/YYYY"),
    },
    drugReportResponseData: {
        data: [],
    },

    drugInfoModal: {}, // Set drug object and it will auto-show
}

export const PrescriptionSlice = createSlice({
    name: "prescription",
    initialState,
    reducers: {
        setCompanyHasPrescription: (state, {payload}) => {
            state.companyHasPrescription = payload;
        },
        // Visits
        setNewVisit: (state, {payload}) => {
            state.newVisit = payload;
        },
        setNewVisitSelectedClient: (state, {payload}) => {
            state.newVisitSelectedClient = payload;
        },
        resetNewVisit: (state) => {
            state.newVisit = newVisitDefaultValue;
            state.newVisitSelectedClient = {};
        },
        setNewVisitOverrideModal: (state, {payload}) => {
            state.newVisitOverrideModal = payload;
        },

        setActiveVisit: (state, {payload}) => {
            state.activeVisit = payload;
        },
        setActiveVisitClientData: (state, {payload}) => {
            state.activeVisitClientData = payload;
        },
        setActiveVisitFirstLoad: (state, {payload}) => {
            state.activeVisitFirstLoad = payload;
        },
        setActiveVisitPrescriptions: (state, {payload}) => {
            state.activeVisitPrescriptions = payload;
        },
        setActiveVisitTreatmentProtocols: (state, {payload}) => {
            state.activeVisitTreatmentProtocols = payload;
        },
        setActiveVisitTreatmentProtocolsSteps: (state, {payload}) => {
            state.activeVisitTreatmentProtocolsSteps = payload;
        },
        setActiveVisitReferrals: (state, {payload}) => {
            state.activeVisitReferrals = payload;
        },
        setActiveVisitForceRefresh: (state, {payload}) => {
            state.activeVisitForceRefresh = payload;
        },
        resetActiveVisit: (state) => {
            state.activeVisit = activeVisitDefaultValue;
            state.activeVisitClientData = null;
            state.activeVisitPrescriptions = [];
            state.activeVisitTreatmentProtocols = [];
            state.activeVisitTreatmentProtocolsSteps = [];
        },

        setEditVisit: (state, {payload}) => {
            state.editVisit = payload;
        },
        setEditVisitLoading: (state, {payload}) => {
            state.editVisitLoading = payload;
        },
        setEditVisitReload: (state, {payload}) => {
            state.editVisitReload = payload;
        },

        // Prescriptions
        setNewPrescription: (state, {payload}) => {
            state.newPrescription = payload;
        },
        resetNewPrescription: (state) => {
            state.newPrescription = newPrescriptionDefaultValue;
        },
        setEditPrescription: (state, {payload}) => {
            state.editPrescription = payload;
        },
        setEditPrescriptionLoading: (state, {payload}) => {
            state.editPrescriptionLoading = payload;
        },
        setEditPrescriptionReload: (state, {payload}) => {
            state.editPrescriptionReload = payload;
        },
        resetEditPrescription: (state) => {
            state.editPrescription = editPrescriptionDefaultValue;
        },

        // Treatment Protocols
        setNewTreatmentProtocol: (state, {payload}) => {
            state.newTreatmentProtocol = payload;
        },
        resetNewTreatmentProtocol: (state) => {
            state.newTreatmentProtocol = newTreatmentProtocolDefaultValue;
        },

        // Referrals
        setNewReferral: (state, {payload}) => {
            state.newReferral = payload;
        },
        resetNewReferral: (state) => {
            state.newReferral = newReferralDefaultValue;
        },
        setEditReferral: (state, {payload}) => {
            state.editReferral = payload;
        },
        setEditReferralLoading: (state, {payload}) => {
            state.editReferralLoading = payload;
        },
        setEditReferralReload: (state, {payload}) => {
            state.editReferralReload = payload;
        },
        resetEditReferral: (state) => {
            state.editReferral = editReferralDefaultValue;
        },

        // Doctor Master Data
        setDoctorTreatmentProtocols: (state, {payload}) => {
            state.doctorTreatmentProtocols = payload;
        },

        // Searches
        setSearchVisits: (state, {payload}) => {
            state.searchVisits = payload;
        },
        setSearchVisitsTotalItems: (state, {payload}) => {
            state.totalItemsV = payload;
        },
        setSearchVisitsPerPage: (state, {payload}) => {
            state.perPageV = payload;
        },

        setSearchPrescriptions: (state, {payload}) => {
            state.searchPrescriptions = payload;
        },
        setSearchPrescriptionsTotalItems: (state, {payload}) => {
            state.totalItemsP = payload;
        },
        setSearchPrescriptionsPerPage: (state, {payload}) => {
            state.perPageP = payload;
        },

        setSearchReferrals: (state, {payload}) => {
            state.searchReferrals = payload;
        },
        setSearchReferralsTotalItems: (state, {payload}) => {
            state.totalItemsR = payload;
        },
        setSearchReferralsPerPage: (state, {payload}) => {
            state.perPageR = payload;
        },

        setSearchICD10s: (state, {payload}) => {
            state.searchICD10s = payload;
        },
        setSearchICD10sTotalItems: (state, {payload}) => {
            state.totalItemsICD10s = payload;
        },
        setSearchICD10sPerPage: (state, {payload}) => {
            state.perPageICD10s = payload;
        },

        setSearchDrugs: (state, {payload}) => {
            state.searchDrugs = payload;
        },
        setSearchDrugsTotalItems: (state, {payload}) => {
            state.totalItemsDrugs = payload;
        },
        setSearchDrugsPerPage: (state, {payload}) => {
            state.perPageDrugs = payload;
        },

        setSearchExams: (state, {payload}) => {
            state.searchExams = payload;
        },
        setSearchExamsTotalItems: (state, {payload}) => {
            state.totalItemsExams = payload;
        },
        setSearchExamsPerPage: (state, {payload}) => {
            state.perPageExams = payload;
        },

        // Prescription Home
        setPrescriptionHome: (state, {payload}) => {
            state.prescriptionHome = payload;
        },

        // Patient History
        setPatientHistory: (state, {payload}) => {
            state.patientHistory = payload;
        },
        setPatientHistoryOptions: (state, {payload}) => {
            state.patientHistoryOptions = payload;
        },
        setPatientHistoryPrescriptionsRequestData: (state, {payload}) => {
            state.patientHistoryPrescriptionsRequestData = payload;
        },
        setPatientHistoryPrescriptions: (state, {payload}) => {
            state.patientHistoryPrescriptions = payload;
        },
        setPatientHistoryReferralsRequestData: (state, {payload}) => {
            state.patientHistoryReferralsRequestData = payload;
        },
        setPatientHistoryReferrals: (state, {payload}) => {
            state.patientHistoryReferrals = payload;
        },
        setPatientHistoryTreatmentProtocols: (state, {payload}) => {
            state.patientHistoryTreatmentProtocols = payload;
        },
        setPatientHistoryFirstLoad: (state, {payload}) => {
            state.patientHistoryFirstLoad = payload;
        },
        softResetPatientHistory: (state) => {
            state.patientHistoryPrescriptions = [];
            state.patientHistoryPrescriptionsRequestData = {...defaultPatientHistoryReq};
            state.patientHistoryReferrals = [];
            state.patientHistoryReferralsRequestData = {...defaultPatientHistoryReq};
            state.patientHistoryTreatmentProtocols = [];
            state.patientHistoryFirstLoad = true;
        },
        resetPatientHistory: (state) => {
            state.patientHistory = {...defaultPatientHistory}
            state.patientHistoryPrescriptions = [];
            state.patientHistoryPrescriptionsRequestData = {...defaultPatientHistoryReq};
            state.patientHistoryReferrals = [];
            state.patientHistoryReferralsRequestData = {...defaultPatientHistoryReq};
            state.patientHistoryTreatmentProtocols = [];
            state.patientHistoryFirstLoad = true;
            state.autoPatientHistory = null;
        },
        setAutoPatientHistory: (state, {payload}) => {
            state.autoPatientHistory = payload;
        },

        // Reports
        setDrugReportRequestData: (state, {payload}) => {
            state.drugReportRequestData = payload;
        },
        setDrugReportResponseData: (state, {payload}) => {
            state.drugReportResponseData = payload;
        },
        setPrescriptionsReportRequestData: (state, {payload}) => {
            state.prescriptionsReportRequestData = payload;
        },
        setPrescriptionsReportResponseData: (state, {payload}) => {
            state.prescriptionsReportResponseData = payload;
        },

        setDrugInfoModal: (state, {payload}) => {
            state.drugInfoModal = payload;
        }
    }
})

export const {
    setCompanyHasPrescription,
    setNewVisit,
    setNewVisitSelectedClient,
    resetNewVisit,
    setNewVisitOverrideModal,

    setActiveVisit,
    setActiveVisitClientData,
    setActiveVisitFirstLoad,
    setActiveVisitPrescriptions,
    setActiveVisitTreatmentProtocols,
    setActiveVisitTreatmentProtocolsSteps,
    setActiveVisitReferrals,
    setActiveVisitForceRefresh,
    resetActiveVisit,

    setEditVisit,
    setEditVisitLoading,
    setEditVisitReload,

    setNewPrescription,
    resetNewPrescription,
    setEditPrescription,
    setEditPrescriptionLoading,
    setEditPrescriptionReload,
    resetEditPrescription,

    setNewTreatmentProtocol,
    resetNewTreatmentProtocol,

    setNewReferral,
    resetNewReferral,
    setEditReferral,
    setEditReferralLoading,
    setEditReferralReload,
    resetEditReferral,

    setDoctorTreatmentProtocols,

    setSearchVisits,
    setSearchVisitsTotalItems,
    setSearchVisitsPerPage,

    setSearchPrescriptions,
    setSearchPrescriptionsTotalItems,
    setSearchPrescriptionsPerPage,

    setSearchReferrals,
    setSearchReferralsTotalItems,
    setSearchReferralsPerPage,

    setSearchICD10s,
    setSearchICD10sTotalItems,
    setSearchICD10sPerPage,

    setSearchDrugs,
    setSearchDrugsTotalItems,
    setSearchDrugsPerPage,

    setSearchExams,
    setSearchExamsTotalItems,
    setSearchExamsPerPage,

    // Prescription Home
    setPrescriptionHome,

    // Patient History
    setPatientHistory,
    setPatientHistoryOptions,
    setPatientHistoryPrescriptionsRequestData,
    setPatientHistoryPrescriptions,
    setPatientHistoryReferralsRequestData,
    setPatientHistoryReferrals,
    setPatientHistoryTreatmentProtocols,
    setPatientHistoryFirstLoad,
    softResetPatientHistory,
    resetPatientHistory,
    setAutoPatientHistory,

    // Reports
    setDrugReportRequestData,
    setDrugReportResponseData,
    setPrescriptionsReportRequestData,
    setPrescriptionsReportResponseData,

    setDrugInfoModal,
} = PrescriptionSlice.actions;
